import React, { FC, useCallback, useMemo } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Form } from '@ant-design/compatible';
import OWAsyncRadio from '../visual_radio_input/VisualRadioInput';
import { connect } from 'react-redux';
import { Button } from 'antd';
import {
	problemTypesRestCrudThunksForBuyer,
	problemTypesTopLevelForBuyer,
} from '../../thunks/problem_types_thunks';
import { assetsRestCrudThunksForBuyer } from '../../thunks/assets_thunks';
import { workOrderAttributesRestCrudThunksForBuyer } from '../../thunks/work_order_attributes_thunks';
import { workOrderPrioritiesRestCrudThunksForBuyer } from '../../thunks/work_order_priorities_thunks';
import { fetchSupplierFacilitiesInPrivateNetworkForBuyer } from '../../thunks/supplier_facilities_thunks';
import {
	getProblemTypeStepStateVariables,
	handleProblemTypeChangeOrClick,
	PT_LEVELS,
	PT_STEP_PROPS,
} from './wo_creation_problem_type_utils';

const TC_NAME = 'WORK_ORDER_PROBLEM_TYPES_TOP_LEVEL_AUTOCOMPLETE';

const WOProblemType: FC<any> = ({
	getCurrentState,
	goBack,
	fieldName = PT_LEVELS.TOP_LEVEL,
	changeId,
	form,
	goNext,
	setState,
	setNTE,

	problemTypes,
	fetchProblemTypesTopLevel,
	fetchMultipleProblemTypes,
	companyConfig,
	currentUser,
	fetchAssets,
	fetchProblemType,
	fetchWorkOrderAttributes,
	fetchSupplierFacilitiesInPrivateNetwork,
	fetchProblemTypes,
	fetchWorkOrderPriority,
}): React.ReactElement => {
	const { getFieldDecorator } = form;

	const isTopLevel = useMemo(() => fieldName === PT_LEVELS.TOP_LEVEL, [fieldName]);

	const ptProps: PT_STEP_PROPS = useMemo(
		() => ({
			fetchAssets,
			goNext,
			setState,
			companyConfig,
			currentUser,
			getCurrentState,
			problemTypes,
			TC_NAME,
			fetchProblemType,
			fetchSupplierFacilitiesInPrivateNetwork,
			fetchWorkOrderAttributes,
			fetchWorkOrderPriority,
			setNTE,
			changeId,
			form,
		}),
		[
			changeId,
			companyConfig,
			currentUser,
			fetchAssets,
			fetchProblemType,
			fetchSupplierFacilitiesInPrivateNetwork,
			fetchWorkOrderAttributes,
			fetchWorkOrderPriority,
			getCurrentState,
			goNext,
			problemTypes,
			setNTE,
			setState,
			form,
		]
	);

	const stateVariables = getProblemTypeStepStateVariables(fieldName);

	const onPTChange = useCallback(
		(id) => {
			handleProblemTypeChangeOrClick(ptProps)(fieldName, id);
		},
		[fieldName, ptProps]
	);

	const onPTClick = useCallback(
		(id) => {
			handleProblemTypeChangeOrClick(ptProps)(fieldName, id, true);
		},
		[fieldName, ptProps]
	);

	const state = useMemo(() => getCurrentState(), [getCurrentState]);

	return (
		<div key={5} className="newWorkOrderWizardPage__section">
			<h3 className="newWorkOrderWizardPage__title">What kind of request is it?</h3>
			<Form.Item className={'flex w-full flex-grow'}>
				{getFieldDecorator(fieldName, {
					rules: [
						{
							required: false,
						},
					],
					initialValue: nullSafeGet(stateVariables.currentLevelId, state),
				})(
					<OWAsyncRadio
						getDescription={(record) => record.description}
						autoPickFirstOption
						mode="default"
						fieldName={fieldName}
						stateSlice={problemTypes}
						additionalFilters={{
							areaId: nullSafeGetOrElse('area.id', state, undefined),
							brandId: nullSafeGet('location.brandId', state),
						}}
						onChange={onPTChange}
						onClick={onPTClick}
						targetCollectionName={TC_NAME}
						fetchMultiple={(ids, targetCollectionName) =>
							fetchMultipleProblemTypes(ids, targetCollectionName)
						}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination = null,
							sorting = null,
							filters = null,
							addToTargetCollection = false
						) => {
							const fnToCall = isTopLevel ? fetchProblemTypesTopLevel : fetchProblemTypes;
							return fnToCall(
								{
									name: searchText,
									...(isTopLevel
										? { industryId: nullSafeGetOrElse('industryId', state, 1) }
										: {
												...(stateVariables.prevLevelId && {
													parentId: nullSafeGet(stateVariables.prevLevelId, state),
												}),
										  }),
								},
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(problemType, records, searchText) =>
							nullSafeGet('length', searchText) ? problemType.hierarchyName : problemType.name
						}
					/>
				)}
			</Form.Item>
			<Button size="large" type="ghost" onClick={goBack}>
				Back
			</Button>
		</div>
	);
};

const mapStateToProps = (state) => ({
	problemTypes: state.problem_types,
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	fetchProblemTypesTopLevel: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			problemTypesTopLevelForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(problemTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
	fetchAssets: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			assetsRestCrudThunksForBuyer.readLite(
				{ ...params, view: 'workOrderCreation' },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchProblemType: (id) => dispatch(problemTypesRestCrudThunksForBuyer.readOne(id)),
	fetchWorkOrderAttributes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			workOrderAttributesRestCrudThunksForBuyer.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchWorkOrderPriority: (id) => dispatch(workOrderPrioritiesRestCrudThunksForBuyer.readOne(id)),
	fetchSupplierFacilitiesInPrivateNetwork: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fetchSupplierFacilitiesInPrivateNetworkForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			problemTypesRestCrudThunksForBuyer.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(WOProblemType);
